import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faHeadset,
  faClipboardCheck,
} from "@fortawesome/free-solid-svg-icons";

import "bootstrap/dist/css/bootstrap.min.css";
import "aos/dist/aos.css";
import AOS from "aos";

import logo from "../assests/oba_logo_nbg.png";

import OBAPageIntroComponent from "../components/common/OBAPageIntroComponent";
import HeaderComponent from "../components/common/HeaderComponent";
import MilestonesTableComponent from "../components/common/MilestonesTableComponent";
import FooterComponent from "../components/common/FooterComponent";
import ErrorModalComponent from "../components/common/ErrorModalComponent";
import LoaderComponent from "../components/common/LoaderComponent";
import apiClient from "../api/apiClient";

export default function MembershipRequestTrackingPage() {
  useEffect(() => {
    AOS.init({ duration: 1000 });
  }, []);
  const location = useLocation();

  const { referenceId: navReferenceId, applicantName } = location.state || {};

  const [referenceId, setReferenceId] = useState(navReferenceId || "");
  const [trackingData, setTrackingData] = useState(null);

  const [loader, setLoader] = useState({
    show: false,
    message: "",
  });

  const [errorModal, setErrorModal] = useState({
    show: false,
    title: "",
    message: "",
  });

  const closeErrorModal = () => {
    setErrorModal({
      show: false,
      title: "",
      message: "",
    });
  };

  // Fetch data automatically if reference ID comes from navigation
  useEffect(() => {
    if (navReferenceId) {
      fetchTrackingData(navReferenceId);
    }
    // eslint-disable-next-line
  }, [navReferenceId]);

  const fetchTrackingData = async (refId = referenceId) => {
    if (!refId.trim()) return;

    try {
      setLoader({
        show: true,
        message: "Loading membership request tracking details. Please wait...",
      });
      const requestBody = {
        requestHeader: {
          requestId: `REQ-${Date.now()}`,
          requestTimestamp: new Date().toISOString(),
        },
        requestData: {
          referenceId: refId,
        },
      };

      const response = await apiClient.post(
        "/v1/memberships/check-request-status",
        requestBody,
      );

      const data = response.data.responseData;

      let milestones = [...data.registrationMilestones];
      const approved = milestones.find(
        (m) => m.status === "APPROVED" && m.completed,
      );
      const rejected = milestones.find(
        (m) => m.status === "REJECTED" && m.completed,
      );

      if (approved || rejected) {
        milestones = milestones.filter(
          (m) =>
            !(m.status === "APPROVED" && !m.completed) &&
            !(m.status === "REJECTED" && !m.completed),
        );
      } else {
        milestones = milestones.filter(
          (m) => m.status !== "APPROVED" && m.status !== "REJECTED",
        );
        milestones.push({
          status: "FINAL",
          displayName:
            "Membership Request Approved/Rejected by Executive Committee",
          completed: false,
          isPending: true,
          reason: null,
          completedAt: null,
        });
      }

      setTrackingData({
        ...data,
        registrationMilestones: milestones,
      });
    } catch (err) {
      setErrorModal({
        show: true,
        title: "Failed to fetch Tracking Data",
        message:
          err.response?.data?.errorMessage ||
          "The Reference ID you entered does not exist. Please check your Reference ID and try again.",
      });
      setTrackingData(null);
    } finally {
      setLoader({
        show: false,
        message: "",
      });
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    try {
      const d = new Date(dateString);
      return d.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div
      className="d-flex flex-column min-vh-100 overflow-x-hidden"
      style={{ backgroundColor: "#f8f9fa" }}
    >
      <HeaderComponent user={null} />
      {/* <NavigationPanelComponent /> */}

      <main className="container my-5 flex-grow-1">
        <ErrorModalComponent
          show={errorModal.show}
          title={errorModal.title}
          message={errorModal.message}
          onClose={closeErrorModal}
        />
        <LoaderComponent
          show={loader.show}
          fullPage={true}
          message={loader.message}
        />
        {/* top header */}
        {!navReferenceId && (
          <>
            {/* Modern Hero Section */}
            <OBAPageIntroComponent
              image={logo}
              title="Track the Status of Your Membership Request"
              description="Enter the Reference ID you received after submitting your membership registration to track the status of your request in real time and stay updated on the progress of your application."
            />
          </>
        )}

        {/* Success Message if navigated from registration */}
        {navReferenceId && applicantName && (
          <div className="row justify-content-center mb-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm rounded-4">
                <div className="card-body p-5">
                  {/* Header */}
                  <div className="text-center mb-4">
                    <div
                      className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                      style={{
                        width: 80,
                        height: 80,
                        backgroundColor: "#198754",
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faCheckCircle}
                        className="text-white"
                        size="3x"
                      />
                    </div>
                    <h4 className="fw-bold mb-3">
                      Your registration has been received!
                    </h4>
                  </div>

                  {/* Body Content */}
                  <p className="mb-3">
                    Dear <strong className="fs-5">{applicantName}</strong>,
                  </p>

                  <p className="mb-3">
                    We have received your membership registration request.
                  </p>

                  <div className="p-3 rounded-3 bg-light mb-3">
                    <strong>Reference ID:</strong>{" "}
                    <span className="text-primary fs-5">{navReferenceId}</span>
                  </div>

                  <p className="mb-3">
                    Please keep this reference ID with you for any further
                    inquiries.
                  </p>

                  <p className="mb-0 fw-bold">
                    It will take approximately{" "}
                    <span className="text-danger">7 working days</span> to
                    process your request.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Search box - Only show if not navigated from registration */}
        {!navReferenceId && (
          <div className="row justify-content-center mb-4" data-aos="fade-up">
            <div className="col-md-8 col-lg-6">
              <div className="card rounded-3 border-vcg-muted">
                <div className="card-body p-4">
                  <div className="d-flex align-items-center mb-3">
                    <h4 className="fw-bold mb-4">
                      Track Membership Request by Reference ID
                    </h4>
                  </div>

                  <div className="input-group input-group-lg">
                    <input
                      type="text"
                      className="form-control"
                      value={referenceId}
                      onChange={(e) => setReferenceId(e.target.value)}
                      placeholder="Enter Reference ID"
                      style={{ borderRadius: "10px 0 0 10px" }}
                    />
                    <button
                      className="btn btn-dark"
                      onClick={() => fetchTrackingData()}
                      disabled={!referenceId.trim()}
                      style={{
                        color: "#fff",
                        borderRadius: "0 10px 10px 0",
                        minWidth: 120,
                      }}
                    >
                      Track
                    </button>
                  </div>
                  <small className="text-muted mt-2 d-block">
                    Example: <code>#OBA_20250919_7118</code>
                  </small>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Timeline + Details */}
        {trackingData && (
          <>
            {/* Application Details + Helper */}
            <div className="row g-4 mb-4">
              <div className="col-lg-8">
                <div className="card rounded-3 border-vcg-muted">
                  <div className="card-body p-4">
                    <h5 className="fw-bold mb-4">
                      <FontAwesomeIcon
                        icon={faClipboardCheck}
                        className="me-2"
                      />
                      Membership Request Status
                    </h5>

                    <div className="row g-3">
                      <div className="col-md-6">
                        <div
                          className="p-3 rounded-3"
                          style={{ background: "#f8f9fa" }}
                        >
                          <small className="text-muted d-block mb-1">
                            Reference ID
                          </small>
                          <h6 className="mb-0 fw-bold text-break">
                            {trackingData.referenceId}
                          </h6>
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div
                          className="p-3 rounded-3"
                          style={{ background: "#f8f9fa" }}
                        >
                          <small className="text-muted d-block mb-1">
                            Membership Number
                          </small>
                          <h6 className="mb-0 fw-bold text-break">
                            {trackingData.membershipNumber || "N/A"}
                          </h6>
                        </div>
                      </div>

                      <div className="col-12">
                        <div
                          className="p-3 rounded-3"
                          style={{ background: "#f8f9fa" }}
                        >
                          <small className="text-muted d-block mb-1">
                            Applicant Name
                          </small>
                          <h6 className="mb-0 fw-bold">
                            {trackingData.applicantName}
                          </h6>
                        </div>
                      </div>

                      <div className="col-12">
                        <div
                          className="p-3 rounded-3"
                          style={{ background: "#f8f9fa" }}
                        >
                          <small className="text-muted d-block mb-1">
                            Current Status
                          </small>
                          <h6 className="mb-0 fw-bold">
                            {trackingData.currentStatusDisplayName}
                          </h6>
                        </div>
                      </div>

                      {trackingData.currentStatus === "REJECTED" && (
                        <div className="col-12">
                          <div
                            className="p-3 rounded-3 border border-danger"
                            style={{ background: "#fff5f5" }}
                          >
                            <small className="text-danger d-block mb-1">
                              Rejection Reason
                            </small>
                            <h6 className="mb-0 fw-bold text-danger">
                              {trackingData.registrationMilestones.find(
                                (m) => m.status === "REJECTED",
                              )?.reason || "No reason provided"}
                            </h6>
                          </div>
                        </div>
                      )}

                      <div className="col-12">
                        <div
                          className="p-3 rounded-3"
                          style={{ background: "#f8f9fa" }}
                        >
                          <small className="text-muted d-block mb-1">
                            Last Updated
                          </small>
                          <h6 className="mb-0 fw-bold">
                            {formatDateTime(trackingData.lastUpdatedDateTime)}
                          </h6>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Side help / contact */}
              <div className="col-lg-4">
                <div className="card rounded-3 border-vcg-muted h-100">
                  <div className="d-flex flex-column card-body text-center p-4 align-items-center justify-content-center">
                    <div
                      className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3 bg-dark"
                      style={{
                        width: 70,
                        height: 70,
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faHeadset}
                        className="text-white"
                        size="2x"
                      />
                    </div>
                    <p className="text-muted mb-2">
                      Need help? Contact our support team
                    </p>

                    {/* Hotline number */}
                    <p className="fw-bold mb-3">Hotline: +94 72 224 1298</p>

                    <a
                      href="mailto:office@vidyalokacollegeoba.com"
                      className="btn btn-dark"
                    >
                      Contact Support
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* MILESTONES TABLE */}
            <MilestonesTableComponent
              title="Membership Request Milestones"
              milestones={trackingData.registrationMilestones}
            />
          </>
        )}
      </main>
      <FooterComponent />
    </div>
  );
}
